/**
 * `next/navigation` — the app-router shim.
 *
 * This is the one the REMOTES actually need. Five exposed dashboards call
 * `useSearchParams()` from next/navigation — CIIDashboard, SiyaEtaDashboard,
 * IHMDashboard, VIRDashboard and voyage-monitoring's ETADashboard — to read the vessel
 * the shell handed them in the URL (`?imo=`, `?vesselName=`, `?dataKey=`).
 *
 * They are pages-router apps importing an app-router hook, which works in Next only
 * because Next's pages adapter backs it with the pages RouterContext. With no Next
 * runtime in the page that adapter has nothing to read, and the failure is silent: the
 * hook returns an empty set and the dashboard renders for no vessel at all, rather than
 * throwing something a smoke test would catch. Hence the share-scope entry in
 * rspack.config.js — the remote must get THIS module, not the one in its own container.
 */

import { useMemo } from 'react';
import {
  useLocation,
  useNavigate,
  useParams as useRouterParams,
  useSearchParams as useRouterSearchParams,
} from 'react-router-dom';

/**
 * Next hands back a ReadonlyURLSearchParams — a URLSearchParams with the mutators
 * removed — and the dashboards only ever call `.get()`. React Router's first tuple
 * element is a live URLSearchParams, which satisfies every read.
 */
export function useSearchParams(): URLSearchParams {
  const [searchParams] = useRouterSearchParams();
  return searchParams;
}

export function usePathname(): string {
  return useLocation().pathname;
}

export function useParams<T = Record<string, string>>(): T {
  return useRouterParams() as T;
}

/**
 * The app-router `useRouter()`. Note the shape differs from next/router's: no `query`,
 * no `pathname`, no `isReady`, and `refresh()` in place of `reload()`. Kept separate
 * from the pages shim for that reason — a remote importing from next/navigation expects
 * this shape, and handing it the pages router would give it fields that quietly do the
 * wrong thing.
 */
export function useRouter() {
  const navigate = useNavigate();

  return useMemo(
    () => ({
      push: (href: string) => navigate(href),
      replace: (href: string) => navigate(href, { replace: true }),
      back: () => navigate(-1),
      forward: () => navigate(1),
      // In Next this re-fetches server components. There are none here, so the honest
      // equivalent of "get the current route's data again" is a reload.
      refresh: () => window.location.reload(),
      prefetch: async () => undefined,
    }),
    [navigate],
  );
}

/** Next throws a special error object here; a plain navigation is the browser equivalent. */
export function redirect(href: string): never {
  window.location.assign(href);
  // Unreachable in practice — assign() does not stop execution synchronously, so the
  // throw is what actually halts the calling render, matching Next's contract that
  // nothing after redirect() runs.
  throw new Error(`redirect(${href})`);
}

export function notFound(): never {
  throw new Error('NEXT_NOT_FOUND');
}
